/**
 * Pain location frequency and quality aggregation.
 *
 * Mirrors the semantics of `calculateLocationFrequencies` in
 * `apps/api/src/routes/pdf-export-utils.ts`, using the `region:laterality`
 * key convention that the export and most web utilities share.
 */

import type {
  PainLocationEntry,
  PainRecord,
} from "./types";
import { mean, roundTo } from "./stats";

// ---------------------------------------------------------------------------
// Result types
// ---------------------------------------------------------------------------

/** Frequency entry for a single pain location (region + laterality). */
export interface LocationFrequency {
  /** V2 body zone, when available */
  zone?: string;
  /** V2 side, when available */
  side?: string;
  /** V2 surface, when available */
  surface?: string;
  /** Body region, e.g. "lower_back" */
  region: string;
  /** Laterality, e.g. "bilateral" */
  laterality: string;
  /** Number of unique days this location was reported */
  daysReported: number;
  /** `daysReported / totalDays * 100`, rounded to 1 decimal */
  percentOfDays: number;
  /** Mean intensity across all occurrences, rounded to 1 decimal */
  avgIntensity: number;
  /** Unique quality descriptors reported at this location, sorted */
  qualities: string[];
}

/** Frequency entry for a pain quality descriptor. */
export interface QualityFrequency {
  /** Quality value, e.g. "sharp" */
  quality: string;
  /** Total occurrences across all records and locations */
  count: number;
  /** `count / totalLocationEntries * 100`, rounded to 1 decimal */
  percentage: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Build a `region:laterality` key — the canonical form used in the export
 * pipeline. Some web utils use `-` instead of `:`, but the shared package
 * standardises on `:`.
 */
export function locationKey(region: string, laterality: string): string {
  return `${region}:${laterality}`;
}

/**
 * Split a `region:laterality` key back into its parts.
 */
export function parseLocationKey(key: string): {
  region: string;
  laterality: string;
} {
  const [region, laterality] = key.split(":");
  return { region, laterality };
}

/**
 * Extract unique, sorted quality values from an array of pain location entries.
 */
export function extractQualities(locations: readonly PainLocationEntry[]): string[] {
  const set = new Set<string>();
  for (const loc of locations) {
    if (loc.qualities) {
      for (const q of loc.qualities) {
        set.add(q.value);
      }
    }
  }
  return Array.from(set).sort();
}

// ---------------------------------------------------------------------------
// Location frequencies
// ---------------------------------------------------------------------------

/**
 * Compute per-location frequency statistics from an array of daily records.
 *
 * The result matches the shape produced by `calculateLocationFrequencies` in
 * `pdf-export-utils.ts`. Results are sorted by `percentOfDays` (descending).
 *
 * @param records  Daily pain records.
 * @param totalDays  Denominator for percentage calculation. Defaults to `records.length`.
 *   Pass a custom value when the caller already knows the true number of days in the range.
 */
export function computeLocationFrequencies(
  records: readonly PainRecord[],
  totalDays?: number,
): LocationFrequency[] {
  if (records.length === 0) return [];

  const effectiveTotalDays = totalDays ?? records.length;

  // Accumulate per-key data in one pass over all records.
  const keyData = new Map<
    string,
    {
      zone?: string;
      side?: string;
      surface?: string;
      region: string;
      laterality: string;
      dates: Set<string>;
      intensities: number[];
      locations: PainLocationEntry[];
    }
  >();

  for (const record of records) {
    for (const loc of record.painLocations) {
      const zone = loc.zone ?? loc.region;
      const side = loc.side ?? loc.laterality;
      const surface = loc.surface ?? "unknown";
      const key = `${zone}:${side}:${surface}`;
      let entry = keyData.get(key);
      if (!entry) {
        entry = {
          zone: loc.zone,
          side: loc.side,
          surface: loc.surface,
          region: loc.region,
          laterality: loc.laterality,
          dates: new Set(),
          intensities: [],
          locations: [],
        };
        keyData.set(key, entry);
      }
      entry.dates.add(record.recordDate);
      entry.intensities.push(loc.intensityScore);
      entry.locations.push(loc);
    }
  }

  const result: LocationFrequency[] = [];
  for (const entry of keyData.values()) {
    const daysReported = entry.dates.size;
    const avgIntensity = mean(entry.intensities) ?? 0;
    result.push({
      zone: entry.zone,
      side: entry.side,
      surface: entry.surface,
      region: entry.region,
      laterality: entry.laterality,
      daysReported,
      percentOfDays: roundTo(
        effectiveTotalDays > 0 ? (daysReported / effectiveTotalDays) * 100 : 0,
        1,
      ) as number,
      avgIntensity: (roundTo(avgIntensity, 1) ?? 0) as number,
      qualities: extractQualities(entry.locations),
    });
  }

  return result.sort((a, b) => b.percentOfDays - a.percentOfDays);
}

// ---------------------------------------------------------------------------
// Quality frequencies
// ---------------------------------------------------------------------------

/**
 * Compute quality frequencies across all pain location entries.
 *
 * Results are sorted by `count` (descending).
 */
export function computeQualityFrequencies(
  records: readonly PainRecord[],
): QualityFrequency[] {
  const counts = new Map<string, number>();
  let totalEntries = 0;

  for (const r of records) {
    for (const loc of r.painLocations) {
      if (loc.qualities) {
        for (const q of loc.qualities) {
          counts.set(q.value, (counts.get(q.value) ?? 0) + 1);
          totalEntries++;
        }
      }
    }
  }

  const result: QualityFrequency[] = [];
  for (const [quality, count] of counts) {
    result.push({
      quality,
      count,
      percentage: (roundTo(
        totalEntries > 0 ? (count / totalEntries) * 100 : 0,
        1,
      ) ?? 0) as number,
    });
  }

  return result.sort((a, b) => b.count - a.count);
}
